import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { supabase } from '@/lib/supabase';
import { Heart } from '@/components/ui/Heart';
import { FloatingHearts } from '@/components/motifs/FloatingHearts';

export const Route = createFileRoute('/login')({ component: Login });

function Login() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (error) setError(error.message);
    else setSent(true);
  }

  return (
    <main className="login">
      <FloatingHearts density={6} />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="login__card"
      >
        <Heart size={56} className="a-breathe" />

        <h1 className="login__title">
          <span style={{ fontStyle: 'italic', color: 'var(--c-rose-deep)' }}>our</span>
          <span> places, kept</span>
        </h1>

        <p className="login__sub">
          Sign in with the email you've always used.
          A magic link will be on its way.
        </p>

        {!sent ? (
          <form onSubmit={send} className="login__form">
            <input
              type="email"
              placeholder="you@somewhere.lovely"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              className="login__input"
            />
            <button type="submit" disabled={busy} className="login__submit">
              {busy ? 'sending…' : 'send magic link'}
            </button>
            {error && <p className="login__error">{error}</p>}
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="login__sent"
          >
            <p>Sent. Check your inbox — the link will bring you straight here.</p>
          </motion.div>
        )}

        <p className="login__footnote">
          for two.
        </p>
      </motion.div>

      <style>{loginCss}</style>
    </main>
  );
}

const loginCss = `
.login {
  position: relative;
  display: grid; place-items: center;
  min-height: 100dvh;
  padding: var(--space-5);
  overflow: hidden;
}
.login__card {
  position: relative; z-index: 1;
  width: min(420px, 100%);
  background: var(--c-paper);
  border-radius: var(--r-xl);
  padding: var(--space-8) var(--space-6);
  box-shadow: var(--shadow-lg);
  text-align: center;
  border: 1px solid var(--c-border-soft);
}
.login__title {
  font-family: var(--font-display);
  /* Smaller upper bound — let weight, italic, and color contrast carry the
     hierarchy instead of size. Larger letter-spacing for editorial feel. */
  font-size: clamp(1.6rem, 5vw, 2.4rem);
  font-weight: 400;
  letter-spacing: 0.005em;
  margin-top: var(--space-4);
  margin-bottom: var(--space-2);
  line-height: 1.1;
}
.login__sub {
  color: var(--c-ink-soft);
  font-size: var(--t-small);
  margin-bottom: var(--space-6);
  font-family: var(--font-hand);
  font-size: 1.2rem;
}
.login__form { display: grid; gap: var(--space-3); }
.login__input {
  background: var(--c-cream);
  border: 1px solid var(--c-border);
  border-radius: var(--r-md);
  padding: 14px 16px;
  font-size: var(--t-body);
  font-family: inherit;
  color: var(--c-ink);
  transition: border-color var(--d-base) var(--ease-soft),
              box-shadow var(--d-base) var(--ease-soft);
}
.login__input:focus {
  outline: none;
  border-color: var(--c-rose-deep);
  box-shadow: 0 0 0 4px var(--c-blush);
}
.login__submit {
  background: linear-gradient(135deg, var(--c-rose) 0%, var(--c-rose-deep) 100%);
  color: white;
  border: none;
  border-radius: var(--r-md);
  padding: 14px 16px;
  font-weight: 500;
  font-size: var(--t-body);
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: transform var(--d-quick) var(--ease-spring),
              box-shadow var(--d-base) var(--ease-soft);
}
.login__submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}
.login__submit:active { transform: translateY(0) scale(0.98); }
.login__submit:disabled { opacity: 0.6; cursor: progress; }
.login__error {
  color: var(--c-danger);
  font-size: var(--t-small);
  margin-top: var(--space-2);
}
.login__sent {
  background: var(--c-mint);
  border-radius: var(--r-md);
  padding: var(--space-4);
  color: var(--c-ink);
}
.login__footnote {
  margin-top: var(--space-6);
  color: var(--c-ink-faint);
  font-size: var(--t-caption);
  letter-spacing: 0.06em;
  text-transform: lowercase;
}
`;
